import { Navigate, useLocation } from "react-router-dom";

import GlobalLoadingPage from "../../../core/components/GlobalLoadingPage";
import { useAuth } from "../../../auth/useAuth";

const GUARD_DEBUG_PREFIX = "[ChurchRequireAuth]";
const CHURCH_PENDING_REDIRECT_KEY = "oikos.church.pendingRedirect";
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function shouldLogGuardDebug() {
  if (typeof window === "undefined") return process.env.NODE_ENV !== "production";

  return (
    process.env.NODE_ENV !== "production" ||
    window.localStorage.getItem("oikos.church.debugAuth") === "true"
  );
}

function describeUser(user) {
  if (!user) return { present: false };

  return {
    present: true,
    id: String(user.id || "").slice(0, 8),
    email: user.email || "",
    isChurchOrgUser: Boolean(user.isChurchOrgUser),
  };
}

function guardDebug(message, details = {}) {
  if (!shouldLogGuardDebug()) return;
  console.info(GUARD_DEBUG_PREFIX, message, details);
}

function getTrailingChurchOrgId(pathname = "") {
  const parts = String(pathname || "")
    .split("/")
    .filter(Boolean);
  const tail = parts.length ? decodeURIComponent(parts[parts.length - 1]) : "";
  return UUID_PATTERN.test(tail) ? tail : "";
}

function getChurchOrgIdFromLocation(location) {
  const pathOrgId = getTrailingChurchOrgId(location.pathname);
  if (pathOrgId) return pathOrgId;

  const params = new URLSearchParams(location.search || "");
  return (
    params.get("org") ||
    params.get("accountId") ||
    params.get("account") ||
    params.get("churchId") ||
    params.get("church") ||
    ""
  );
}

function getPathFromLocation(location) {
  return `${location.pathname || "/church"}${location.search || ""}${location.hash || ""}`;
}

function storePendingRedirect(redirectPath = "") {
  if (typeof window === "undefined" || !redirectPath || redirectPath === "/church") return;

  try {
    window.sessionStorage.setItem(CHURCH_PENDING_REDIRECT_KEY, redirectPath);
  } catch (_error) {
    // Ignore private browsing or storage permission failures.
  }
}

export default function ChurchRequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <GlobalLoadingPage
        title="Loading Church"
        detail="Restoring your Church session and access..."
      />
    );
  }

  if (!user) {
    guardDebug("redirecting to login: no user", { path: location.pathname });
    const orgParam = getChurchOrgIdFromLocation(location);
    const redirectPath = getPathFromLocation(location);
    storePendingRedirect(redirectPath);
    const redirectSearch = `?redirect=${encodeURIComponent(redirectPath)}`;
    const loginPath = orgParam
      ? `/church/login/${encodeURIComponent(orgParam)}${redirectSearch}`
      : `/church/login${redirectSearch}`;
    return <Navigate to={loginPath} state={{ from: location }} replace />;
  }

  guardDebug("rendering protected Church content", {
    path: location.pathname,
    user: describeUser(user),
  });
  return children;
}
